import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

interface Props {
  firstName?: string | null;
  email?: string | null;
  subheading: ReactNode;
  chipLabel: string;
  title: string;
  body: ReactNode;
  leadingIcon?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}

function greetingName(firstName?: string | null, email?: string | null): string | null {
  const trimmed = firstName?.trim();
  if (trimmed) return trimmed;
  if (email) {
    const local = email.split('@')[0];
    if (local) return local;
  }
  return null;
}

// Shared shell for dashboard onboarding states (setup-paused, awaiting team
// approval). Owns the "Welcome, {greeting}!" header, intro paragraph, and
// the left-border panel chrome; callers fill the chip/title/body/action
// slots and optionally render extra content (action cards) below.
export function DashboardOnboardingBanner({
  firstName,
  email,
  subheading,
  chipLabel,
  title,
  body,
  leadingIcon,
  action,
  children,
}: Props): JSX.Element {
  const greeting = greetingName(firstName, email);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
          {greeting ? `Welcome, ${greeting}!` : 'Welcome to OnFrontiers'}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {subheading}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.200',
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
          borderRadius: 2,
          px: 3,
          py: 2.5,
          mb: children ? 3 : 0,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        {leadingIcon}
        <Box sx={{ flex: 1 }}>
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              bgcolor: 'rgba(0, 121, 107, 0.1)',
              color: 'var(--brandPrimary)',
              fontWeight: 600,
              mb: 1.25,
              borderRadius: '999px',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {body}
          </Typography>
        </Box>
        {action}
      </Box>

      {children}
    </Box>
  );
}
